import { productImageUrl, variantImageUrl } from "./mockIngestedProduct";
import type {
  MediaLibraryAssetItem,
  MediaLibraryListParams,
  MediaLibraryListResponse,
} from "@/types/catalogAssets";

type MockOwner = Pick<
  MediaLibraryAssetItem,
  "product_id" | "product_title" | "product_handle"
> & {
  groups: { collections: string[]; categories: string[]; tags: string[] };
};

type MockMediaAsset = MediaLibraryAssetItem & Pick<MockOwner, "groups">;

const ACME: MockOwner = {
  product_id: "SKU-AAA",
  product_title: "Acme Widget",
  product_handle: "sku-aaa",
  groups: {
    collections: ["Bestsellers"],
    categories: ["Widgets"],
    tags: ["new"],
  },
};
const BETA: MockOwner = {
  product_id: "SKU-BBB",
  product_title: "Beta Gadget",
  product_handle: "sku-bbb",
  groups: {
    collections: ["Featured"],
    categories: ["Gadgets"],
    tags: ["sale"],
  },
};
const GAMMA: MockOwner = {
  product_id: "SKU-CCC",
  product_title: "Gamma Gizmo",
  product_handle: "sku-ccc",
  groups: {
    collections: ["Bestsellers"],
    categories: ["Gizmos"],
    tags: ["new", "vip"],
  },
};
const UNOWNED: MockOwner = {
  product_id: "",
  product_title: "",
  product_handle: "",
  groups: { collections: [], categories: [], tags: [] },
};

function asset(
  id: number,
  handle: string,
  owner: MockOwner,
  overrides: Partial<MediaLibraryAssetItem> = {},
): MockMediaAsset {
  return {
    id,
    handle,
    url: productImageUrl,
    title: "",
    type: "image",
    alt: "",
    origin: "PDP_HERO",
    width_px: 1200,
    height_px: 1200,
    file_size: 245_760,
    file_name: `${handle}.png`,
    mime_type: "image/png",
    upload_status: "uploaded",
    approval_status: "approved",
    source: "ingested",
    variant_id: "",
    ...owner,
    ...overrides,
  };
}

const MOCK_MEDIA_LIBRARY: MockMediaAsset[] = [
  asset(1, "acme-hero", ACME, { title: "Acme Widget hero" }),
  asset(2, "acme-social", ACME, {
    origin: "SOCIAL",
    source: "ai_generated",
    url: variantImageUrl,
  }),
  asset(3, "acme-variant-blue", ACME, {
    variant_id: "SKU-AAA-BLUE",
    origin: "PDP_ALT",
    url: variantImageUrl,
  }),
  asset(4, "beta-hero", BETA, { title: "Beta Gadget hero" }),
  asset(5, "beta-promo", BETA, {
    type: "video",
    origin: "SOCIAL",
    mime_type: "video/mp4",
    file_name: "beta-promo.mp4",
    width_px: 1920,
    height_px: 1080,
    file_size: 4_128_000,
  }),
  asset(6, "gamma-hero", GAMMA, { title: "Gamma Gizmo hero" }),
  asset(7, "site-banner", UNOWNED, { title: "Site banner", origin: null }),
];

export const MOCK_MEDIA_LIBRARY_DEFAULT_LIMIT = 50;

function stripGroups(item: MockMediaAsset): MediaLibraryAssetItem {
  const {
    id,
    handle,
    url,
    title,
    type,
    alt,
    origin,
    width_px,
    height_px,
    file_size,
    file_name,
    mime_type,
    upload_status,
    approval_status,
    source,
    product_id,
    product_title,
    product_handle,
    variant_id,
  } = item;
  return {
    id,
    handle,
    url,
    title,
    type,
    alt,
    origin,
    width_px,
    height_px,
    file_size,
    file_name,
    mime_type,
    upload_status,
    approval_status,
    source,
    product_id,
    product_title,
    product_handle,
    variant_id,
  };
}

export function resolveMockMediaLibrary(
  params: MediaLibraryListParams,
): MediaLibraryListResponse {
  const { collection, category, tag, type, approval_status } = params;
  let items = MOCK_MEDIA_LIBRARY;
  if (collection) {
    items = items.filter((a) => a.groups.collections.includes(collection));
  } else if (category) {
    items = items.filter((a) => a.groups.categories.includes(category));
  } else if (tag) {
    items = items.filter((a) => a.groups.tags.includes(tag));
  }
  if (type?.length) {
    items = items.filter((a) => type.includes(a.type));
  }
  if (approval_status?.length) {
    items = items.filter((a) => approval_status.includes(a.approval_status));
  }
  if (params.sort_by === "newest") {
    items = [...items].reverse();
  }
  const offset = params.next ?? 0;
  const limit = params.limit ?? MOCK_MEDIA_LIBRARY_DEFAULT_LIMIT;
  const page = items.slice(offset, offset + limit);
  const next = offset + page.length < items.length ? offset + limit : null;
  return {
    assets: page.map(stripGroups),
    next,
    count: items.length,
  };
}
